import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Media } from '@/types/media';
import { TYPE_LABELS } from '@/types/media';

interface BannerProps {
  items: Media[];
}

export default function Banner({ items }: BannerProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const current = items[index];

  const goPrev = () => setIndex((prev) => (prev - 1 + items.length) % items.length);
  const goNext = () => setIndex((prev) => (prev + 1) % items.length);

  return (
    <section className="w-full px-4 md:px-8 pt-5 md:pt-8">
      <div className="relative w-full rounded-lg overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-700">
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-accent-500/25 blur-3xl"></div>
        <div className="absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-primary-400/25 blur-3xl"></div>

        <div className="relative grid grid-cols-1 lg:grid-cols-2 items-center gap-6 md:gap-10 p-6 md:p-12 min-h-[420px] md:min-h-[520px]">
          <div key={current.id} className="animate-fade-up">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold">
              <i className="ri-fire-fill"></i>
              焦点推荐 · {TYPE_LABELS[current.type]}
            </span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-background-50 leading-tight">
              {current.title}
            </h1>
            <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm md:text-base text-background-200">
              <span>{current.region}</span>
              <span className="text-background-400">|</span>
              <span>{current.year}</span>
              <span className="text-background-400">|</span>
              <span>{current.genres.join(' / ')}</span>
              <span className="text-background-400">|</span>
              <span className="text-accent-400 font-semibold">{current.rating} 分</span>
            </p>
            <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-3 max-w-xl">
              {current.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${current.type}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-200/50 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览更多{TYPE_LABELS[current.type]}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="relative hidden lg:flex items-center justify-center">
            <div className="relative w-[260px] xl:w-[300px] aspect-[2/3] rounded-lg overflow-hidden ring-1 ring-background-50/20">
              <img
                src={current.cover}
                alt={`${current.title} 焦点海报`}
                title={`${current.title} - 神马视频影视资源`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/60 to-transparent"></div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 md:bottom-6 right-4 md:right-8 flex items-center gap-3">
          <button
            type="button"
            onClick={goPrev}
            aria-label="上一张"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="下一张"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {items.map((item, dotIndex) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => setIndex(dotIndex)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                dotIndex === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/40 hover:bg-background-50/70'
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}